import AppContext from 'App/AppContext';
import { IPLabel } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { AIActionsButton, AIActionsButtonVariant } from 'components/Buttons/AIActionsButton';
import { useCopilotFieldProposal } from 'components/Copilot/TextActions/useCopilotFieldProposal';
import { isCopilotChatAvailable, isCopilotPanelAvailable } from 'components/Copilot/copilotPanelAvailability';
import TextInfo from 'components/Notification/Info';
import { AcceptType } from 'globalConstants';
import { getEntityTypeName } from 'globalFunctions';
import { globalStackTokensGapExtraSmall } from 'globalStyles';
import { TFunction } from 'i18next';
import { getTextFieldBindings } from 'models/copilot/actions/aiTextActionTemplates';
import { CopilotActiveField, CopilotFieldBinding, CopilotFieldProposalDecision } from 'models/copilot/copilotField';
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clearHtmlValue, sanitizeHtml } from 'utils/html';
import RichTextEditorMCE, { IRichTextEditorMCE } from './RichTextEditorMCE';
import RichTextReadOnly from './RichTextReadOnly';
import type { Editor as TinyMCEEditor } from 'tinymce';

export interface IRichTextEditor {
  focus: () => void;
  blur: () => void;
}

export interface IRichTextEditorProps {
  className?: string;
  html?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  placeholderReadonly?: string;
  height?: string | number;
  maxHeight?: number | undefined; // -1 for unlimited
  // The least height in pixels, also while the editor grows with its content.
  minHeight?: number;
  maxLength?: number | undefined;
  readOnly?: boolean;
  onBadgeClick?: (badgeText: string) => void;
  onStartEdit?: () => void;
  onBlur?: (lastValue: string | undefined) => void;
  onFocus?: () => void;
  onKeyUp?: (event: Partial<React.KeyboardEvent>) => void;
  onKeyDown?: (event: Partial<React.KeyboardEvent>) => void;
  onAccept?: (value: string | undefined, accept: boolean) => void;
  /**
   * The entity field this editor binds, with the label the user sees for it. Binding a field gives the
   * editor its AI button: text actions that run in the Copilot panel with the field as their subject,
   * and the free question. Without a binding there is no AI button.
   */
  aiField?: CopilotFieldBinding;
  // The field label, rendered in one row with the AI button above the editor.
  label?: string;
  // An info tooltip next to the label.
  labelInfo?: string;
  unFocusKey?: string;
  directEdit?: boolean;
  hideBorder?: boolean;
  hideEditIcon?: boolean;
  stripParagraph?: boolean;
  disallowFullscreen?: boolean;
  compactToolbar?: boolean;
  hideRemainingChars?: boolean;
  disableAutoFocus?: boolean;
  toolbarOnTop?: boolean;
  // Docks the toolbar at the top of the nearest scrolling ancestor while that ancestor scrolls.
  // Menus and popups render next to the editor and stay inside that ancestor.
  stickyToolbar?: boolean;
  // Cleans what AI tools leave in pasted content (markdown symbols, invisible characters, citation markers,
  // inline styles) and tells the user once per paste. Off by default so other editors are unaffected.
  cleanAiPaste?: boolean;
  // When set, the editor offers the image plugin and uploads every inserted, pasted or dropped image through
  // this callback, which returns the URL the image is served from. Without it, the editor has no images.
  onImageUpload?: (file: Blob, fileName: string) => Promise<string>;
  // The image types the editor accepts with onImageUpload; defaults to allowedWebImageMimeTypes.
  acceptedImageTypes?: AcceptType;
  // Called instead of inserting when the pasted content is a list of 2+ lines; see IRichTextEditorMCEProps.
  onPasteList?: (items: string[], pastedContent: string) => void;
  // Called in TinyMCE's setup of the editor, before it renders: a caller registers its own options and event
  // handlers there without changing the other editors of the app.
  onSetup?: (editor: TinyMCEEditor) => void;
}

const RichTextEditor = forwardRef(function RichTextEditor(props: IRichTextEditorProps, ref) {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation']);
  const [focus, setFocus] = useState<boolean>(false);
  // The cleaned html the editor starts from, kept for the value it was made from. Starting an edit
  // must not change the value by itself: only a real edit reaches onChange.
  const [startHtml, setStartHtml] = useState<{ from?: string; to?: string }>();
  const editorRef = useRef<IRichTextEditorMCE | null>(null);
  // the element the field lives in: focused and scrolled to when a proposal lands, which also expands
  // a collapsed accordion around it
  const hostRef = useRef<HTMLDivElement>(null);
  // a proposal landed while the editor was not mounted: place the caret once TinyMCE is ready
  const pendingFocusRef = useRef<boolean>(false);
  const maxLength = props.maxLength ?? 4000;
  const isEditing = props.directEdit === true || (!props.readOnly && focus);

  useEffect(() => {
    if (props.unFocusKey) {
      setFocus(false);
    }
  }, [props.unFocusKey]);

  useEffect(() => {
    if (props.directEdit === false) {
      setFocus(false);
    } else if (props.directEdit === true) {
      setFocus(true);
    }
  }, [props.directEdit]);

  useImperativeHandle(
    ref,
    (): IRichTextEditor => {
      return {
        focus() {
          editorRef.current?.focus();
        },
        blur() {
          onBlur(undefined, true);
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // The one way into edit mode: from a click on the read-only view, or when a proposal landed while the
  // field was in view mode (then with the value it just became). The html the editor starts from is
  // cleaned; the value itself is left alone.
  const startEdit = (html: string | undefined) => {
    setFocus(true);
    if (props.onStartEdit) {
      setStartHtml({ from: html, to: clearHtmlValue(html, props.stripParagraph ?? false, true, true) });
      props.onStartEdit();
    }
  };

  const onFocus = () => startEdit(props.html);

  const onBlur = (lastValue: string | undefined, hasChanged: boolean) => {
    // with the Accept/Cancel buttons the field returns to read-only on blur
    if (props.onAccept) {
      setFocus(false);
    }
    if (props.onBlur) {
      //nothing was edited: hand back the value as it was, not the editor's re-serialized copy of it
      props.onBlur(hasChanged ? lastValue : props.html);
    }
  };

  const editorHtml = startHtml && startHtml.from === props.html && startHtml.to ? startHtml.to : props.html;

  //
  // AI actions
  //
  const aiField = props.aiField;
  const showAIActions = !!aiField && !props.readOnly && isCopilotPanelAvailable(appContext);

  // the editor's content while it is mounted, the stored value otherwise
  const getText = (): string => (isEditing ? editorRef.current?.getContent() : undefined) ?? props.html ?? '';

  // the field and the text it holds when the user clicks: the actual value the assistant works on
  const getActiveField = (): CopilotActiveField | undefined =>
    aiField
      ? {
          entityType: aiField.entityType,
          entityId: aiField.entityId,
          field: aiField.field,
          text: getText(),
          entityName: aiField.entityName,
        }
      : undefined;

  const getAIBindings = () => {
    if (!aiField) return [];

    return getTextFieldBindings(
      {
        field: aiField,
        fieldLabel: aiField.label,
        entityLabel: getEntityTypeName(aiField.entityType, t as TFunction<string[]>),
      },
      { hasText: getText().trim().length > 0, chatAvailable: isCopilotChatAvailable(appContext) },
    );
  };

  // Brings the field into view before a proposal lands in it. Focusing the host first also expands a
  // collapsed accordion around the field, which listens for focus inside it.
  const bringIntoView = () => {
    hostRef.current?.focus({ preventScroll: true });
    hostRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  /**
   * Applies the text the user accepted on the panel's proposal card: replacing the field or inserting
   * at the caret while the editor is mounted, otherwise through the stored value (insert appends: there
   * is no caret). Unsaved either way; the scene's Save and Discard take over. The editor then opens and
   * takes focus so the user can adjust the result.
   */
  const applyProposal = (html: string, decision: CopilotFieldProposalDecision) => {
    const clean = sanitizeHtml(html);
    const nextLength = decision === 'insert' ? getText().length + clean.length : clean.length;
    if (nextLength > maxLength) {
      const message = t('translation:General.Text.MaxLengthExceeded', { max: maxLength });
      appContext.showNotification(message, true, false, true);

      return;
    }

    bringIntoView();

    if (isEditing && editorRef.current) {
      if (decision === 'insert') {
        editorRef.current.insertContent(clean);
      } else {
        editorRef.current.setContent(clean);
      }
      editorRef.current.focus();

      return;
    }

    const next = decision === 'insert' ? getText() + clean : clean;
    props.onChange(next);
    pendingFocusRef.current = true;
    startEdit(next);
  };

  useCopilotFieldProposal(showAIActions ? aiField : undefined, applyProposal);

  const onEditorInit = () => {
    if (!pendingFocusRef.current) return;

    pendingFocusRef.current = false;
    editorRef.current?.focus();
  };

  //
  // Main render
  //
  const renderEditor = () => {
    if (isEditing) {
      return <RichTextEditorMCE ref={editorRef} {...props} html={editorHtml} onBlur={onBlur} onInit={onEditorInit} />;
    }

    return (
      <RichTextReadOnly
        onClick={props.readOnly ? undefined : () => onFocus()}
        {...props}
        placeholder={props.placeholderReadonly || props.placeholder}
        linkify={true}
      />
    );
  };

  const renderAIActions = () => (
    <AIActionsButton
      variant={props.compactToolbar ? AIActionsButtonVariant.Icon : AIActionsButtonVariant.Action}
      bindings={getAIBindings()}
      getActiveField={getActiveField}
    />
  );

  const renderLabel = () => {
    const label = <IPLabel>{props.label}</IPLabel>;

    return props.labelInfo ? <TextInfo message={props.labelInfo}>{label}</TextInfo> : label;
  };

  if (!showAIActions && !props.label) return renderEditor();

  return (
    <div ref={hostRef} tabIndex={-1} style={{ outline: 'none' }}>
      <IPStack tokens={globalStackTokensGapExtraSmall}>
        {props.label ? (
          <IPStack horizontal verticalAlign="center" horizontalAlign="space-between">
            <IPStack.Item>{renderLabel()}</IPStack.Item>
            {showAIActions && <IPStack.Item>{renderAIActions()}</IPStack.Item>}
          </IPStack>
        ) : (
          <IPStack.Item align="end">{renderAIActions()}</IPStack.Item>
        )}
        <IPStack.Item>{renderEditor()}</IPStack.Item>
      </IPStack>
    </div>
  );
});

export default RichTextEditor;
